/**
 * Asking the browser where it is, and the rules about when that is allowed.
 *
 * ## The one rule that matters
 *
 * **Never prompt on load.** A permission dialog thrown at someone who has just opened a landscape
 * editor is asking for a decision they have no context for, about a feature they have not found yet,
 * and the usual answer is a permanent "Block" that costs the feature forever. So this module splits
 * the question in two:
 *
 * - {@link readGrantedPosition} answers only if permission has *already* been granted, and returns
 *   undefined without so much as a flicker otherwise. Safe to call on startup.
 * - {@link requestPosition} will prompt, and is only ever reached by someone pressing a button
 *   labelled with what it does.
 *
 * ## Why a postal code comes back rather than a coordinate
 *
 * The climate table is keyed by postal district, so a latitude means nothing to it until it has been
 * reverse-geocoded. That is a network call to a free service, which is exactly why it is not on the
 * startup path either: {@link readGrantedPosition} gives coordinates, and the caller decides whether
 * turning them into a ZIP is worth a request.
 *
 * Every failure here — no permission, no geolocation API, a timeout, a geocoder having a bad day —
 * returns undefined rather than throwing. This is a convenience behind a button; the fallback is a
 * person typing five digits, which is not a hardship and must never be presented as an error.
 */

import { reverseGeocodePostalCode } from '@/geo/Geocoding';
import type { LatLng } from '@/geo/GeoMath';
import { normalizePostalCode } from './PostalZones';

/** How long to wait for a fix before giving up and leaving the field to be typed in. */
const POSITION_TIMEOUT_MS = 8000;

/**
 * Our own deadline, over the top of the one `getCurrentPosition` takes.
 *
 * The browser's `timeout` option does **not** run while its permission prompt is on screen — it
 * starts once permission is decided. So a prompt the user ignores, or dismisses with the Escape key,
 * leaves `getCurrentPosition` having called neither callback, forever. Anything awaiting it waits
 * forever with it, which is how a button ends up permanently disabled and the control beside it
 * silently refusing to act.
 *
 * This guarantees the promise settles whatever the browser does.
 */
const HARD_DEADLINE_MS = POSITION_TIMEOUT_MS + 4000;

/** A cached fix up to five minutes old is fine — a garden does not move. */
const POSITION_MAX_AGE_MS = 5 * 60 * 1000;

function getPosition(): Promise<LatLng | undefined> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: LatLng | undefined): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(undefined), HARD_DEADLINE_MS);
    navigator.geolocation.getCurrentPosition(
      (position) => finish({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => finish(undefined),
      { timeout: POSITION_TIMEOUT_MS, maximumAge: POSITION_MAX_AGE_MS },
    );
  });
}

/**
 * Whether location has already been granted, without asking.
 *
 * `navigator.permissions` is absent in a few browsers and throws on an unrecognised name in others,
 * so both are treated as "we cannot know" — which is deliberately the same answer as "not granted",
 * because the only safe action under uncertainty is to stay quiet.
 */
export async function locationAlreadyGranted(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return false;
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

/**
 * The browser's position, but only if that costs no prompt. Safe on startup.
 */
export async function readGrantedPosition(): Promise<LatLng | undefined> {
  if (!(await locationAlreadyGranted())) return undefined;
  return getPosition();
}

/**
 * The browser's position, prompting if necessary. Only ever called from a button press.
 */
export async function requestPosition(): Promise<LatLng | undefined> {
  return getPosition();
}

/** Turn a fix into a postal code the climate table can read, or undefined. */
export async function postalCodeAt(at: LatLng): Promise<string | undefined> {
  const raw = await reverseGeocodePostalCode(at);
  // Nominatim returns `08836` in the US but `SW1A 1AA` and `75008` elsewhere, and only the first is
  // something this table can answer. Normalizing here means the caller sees a usable code or nothing.
  return raw === undefined ? undefined : normalizePostalCode(raw);
}

/**
 * The whole convenience in one call: position, then postal code.
 *
 * `prompt` decides which half of the permission rule applies — false on startup, true from a button.
 */
export async function detectPostalCode(options: { prompt: boolean }): Promise<string | undefined> {
  const at = options.prompt ? await requestPosition() : await readGrantedPosition();
  return at === undefined ? undefined : postalCodeAt(at);
}
